import { useState } from 'react'
import ListingCard from './ListingCard.jsx'
import { noteFor } from '../util.js'

/**
 * One dealer's panel in the dealer view.
 *
 * Working and Interested bikes stay inside their dealer's panel here rather
 * than being hoisted to the top of the page: this view answers "who am I
 * calling next", and removing the bike from the panel would remove the reason
 * for opening it.
 */
export default function DealerGroup({
  group, dealer = {}, notes, onOpen, onStatus, defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen)
  const hasContact = Boolean(dealer.salesperson || dealer.phone || dealer.email)
  const count = group.listings.length

  // The header is a button, so its content model permits phrasing content
  // only — no h2 nested inside it. Heading semantics come from
  // role="heading" on a span instead (same pattern as ListingCard's title),
  // and the button gets an explicit aria-label rather than letting its
  // accessible name become the concatenation of every string inside it.
  const toggleLabel = `${group.name}, ${count} ${count === 1 ? 'listing' : 'listings'}`

  return (
    <section className="dealer-panel">
      <button
        className="section-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={toggleLabel}
      >
        <span role="heading" aria-level="2" className="dealer-name">
          <span>{group.name}</span> <span className="count">{count}</span>
          {group.hasWorking && (
            // Dot, not the word "Working": each card already carries its own
            // status-pill with that exact label when its note says so, and a
            // second literal "Working" text here would make it ambiguous
            // which one a query — or an operator's eye — is looking at. The
            // aria-label keeps the flag available with the panel collapsed,
            // matching the ListingCard star's aria-label-only-icon convention.
            <span className="status-pill s-working" aria-label="Has a bike being worked">●</span>
          )}
        </span>
        <span className="chev">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <>
          <p className="contact">
            {hasContact ? (
              <>
                {dealer.salesperson && <span>{dealer.salesperson}</span>}
                {dealer.phone && <a href={`tel:${dealer.phone}`}>{dealer.phone}</a>}
                {dealer.email && <a href={`mailto:${dealer.email}`}>{dealer.email}</a>}
              </>
            ) : (
              <span className="muted">No contact saved — open any bike to add one.</span>
            )}
          </p>

          <div className="grid">
            {group.listings.map((l) => (
              // The card's own "Dealer" fact is redundant here — the panel
              // it lives in is already labelled with this dealer's name —
              // so it's suppressed via ListingCard's own showDealer prop
              // rather than by stripping dealerName from the listing itself.
              // The listing handed down (and so the object onOpen/onStatus
              // receive) stays the real, untouched `l`: mapsUrl still builds
              // its query from the real dealerName, and downstream workspace
              // writes (dealerKey, notes — Task 13) still see it too.
              <ListingCard
                key={l.id}
                listing={l}
                note={noteFor(notes, l.id)}
                onOpen={onOpen}
                onStatus={onStatus}
                showDealer={false}
              />
            ))}
          </div>
        </>
      )}
    </section>
  )
}
